import { DateOnly } from '@if/core/date'
import { EDocumentStatus, EDocumentType, EExternalStepStatus, EFinancingProgram } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { Caption, EnumChip, IconTile, StrongValue, UploadAreaButton, getEnumDescriptor, useCan } from '@if/mui'
import { access } from '@src/access'
import { Box, Stack } from '@mui/material'
import { useUploadRequiredDocument } from '@src/data/api/credit-api/credit-api'
import { Constants } from '@src/types/Constants'
import { FilteredWorksheet } from '@src/types/FilteredWorksheet'
import { RequiredExternalStep } from '@src/types/CreditApplicationSchema'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { FileText } from 'lucide-react'
import { ChangeEventHandler, Dispatch, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  canSendOrDeleteHighEstimateInvoice,
  isIdentityVerificationDocument,
  useFullNameText,
} from './documentTableRow-hooks'
import ResendIdentityVerificationButton from './ResendIdentityVerificationButton'

interface Props {
  documentRequired: RequiredDocument
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  setError: Dispatch<Error>
  canEdit: boolean
  worksheet: FilteredWorksheet | null
  identityVerification: RequiredExternalStep | null
  isMerchantDashboardOrigin: boolean
}

const MAX_FILE_SIZE = 20971520 // 20MB

// One document line: icon + name + meta (from/for + dates) + status chip + upload action.
const DocumentRow = ({
  documentRequired,
  creditApplicationId,
  financingProgramId,
  setError,
  canEdit,
  worksheet,
  identityVerification,
  isMerchantDashboardOrigin,
}: Props) => {
  const { t } = useTranslation()
  const name = useFullNameText(documentRequired)
  const [uploadDocument, isUploading] = useUploadRequiredDocument({ creditApplicationId, financingProgramId })
  const isIdentityVerificationEnabled = useCan(access.identityVerification)

  // A credit-agent photo ID backed by a Vouched step is proven through the verification link,
  // so the merchant resends that link instead of uploading anything.
  const isIdentityVerification = isIdentityVerificationDocument(
    documentRequired,
    identityVerification,
    isIdentityVerificationEnabled,
  )
  // An approved ID needs no new link, even if the Vouched step itself never reached Completed.
  const showResendIdentityVerification =
    isIdentityVerification &&
    canEdit &&
    isMerchantDashboardOrigin &&
    documentRequired.status !== EDocumentStatus.Approved &&
    identityVerification?.status !== EExternalStepStatus.Completed

  // Upload allowed while not approved (clinic invoices only within their send window).
  const showUpload = useMemo(() => {
    if (isIdentityVerification) return false
    if (documentRequired.status === EDocumentStatus.Approved) return false
    if (canEdit && documentRequired.typeId !== EDocumentType.ClinicInvoice) return true
    return Boolean(
      worksheet?.isHighEstimate &&
        worksheet.firstPaymentOn &&
        canEdit &&
        documentRequired.typeId === EDocumentType.ClinicInvoice &&
        canSendOrDeleteHighEstimateInvoice(new DateOnly(worksheet.firstPaymentOn)),
    )
  }, [
    canEdit,
    documentRequired.status,
    documentRequired.typeId,
    isIdentityVerification,
    worksheet?.isHighEstimate,
    worksheet?.firstPaymentOn,
  ])

  // Validate size/type/mime then upload the picked files (reuses the existing upload API + constraints).
  const onFilesPicked = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      const { files } = event.target
      if (!files) return
      const filesArray = Array.from(files)
      for (const file of filesArray) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? ''
        const tooBig = file.size > MAX_FILE_SIZE
        const badType = !Constants.SupportedDocumentTypes.includes(fileExtension)
        const badMime =
          !Constants.SupportedDocumentMimeTypes.includes(file.type) && !['heif', 'heic'].includes(fileExtension)
        if (tooBig || badType || badMime) {
          setError(
            new Error(
              t(tooBig ? 'common.errors.fileSizeTooBig' : 'common.errors.unsupportedFileType', {
                supportedTypes: Constants.SupportedDocumentTypes,
              }),
            ),
          )
          window.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }
      }
      await uploadDocument({
        financingProgramId,
        creditApplicationId,
        applicantType: documentRequired.applicantType,
        typeId: documentRequired.typeId,
        subKey: documentRequired.subKey,
        files: filesArray,
      })
    },
    [
      creditApplicationId,
      documentRequired.applicantType,
      documentRequired.subKey,
      documentRequired.typeId,
      financingProgramId,
      setError,
      t,
      uploadDocument,
    ],
  )

  // Meta: origin/step + dates, the agent's note and the refusal reason — tinted like the chip, clipped to one line.
  const originStep = [
    documentRequired.requiredFrom &&
      `${t('documentSectionHeaders.requiredFrom')}: ${t(`ERequiredFrom.${documentRequired.requiredFrom}`, { ns: 'enum' })}`,
    documentRequired.requiredBeforeStep &&
      `${t('documentSectionHeaders.required')}: ${t(`ECreditApplicationStep.${documentRequired.requiredBeforeStep}`, { ns: 'enum' })}`,
  ]
    .filter(Boolean)
    .join('  •  ')
  const dates = [
    `${t('documentSectionHeaders.requestedOn')}: ${formatDate(documentRequired.createdOn)}`,
    documentRequired.receivedOn &&
      `${t('documentSectionHeaders.receivedOn')}: ${formatDate(documentRequired.receivedOn)}`,
  ]
    .filter(Boolean)
    .join('  •  ')
  const note = documentRequired.note && `${t('documentSectionHeaders.notes')}: ${documentRequired.note}`
  const refusalReason =
    documentRequired.refusalReason && `${t('documentSectionHeaders.refusalReason')}: ${documentRequired.refusalReason}`
  const statusColor = getEnumDescriptor(EDocumentStatus, documentRequired.status)?.color ?? 'warning'

  return (
    // Wraps rather than squeezing: the chip and the upload area are `nowrap`, so
    // without a basis on the text block they'd shrink it to one word per line.
    // `flex-basis` on the text makes the action group drop to its own line first.
    <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
      <IconTile icon={FileText} size="sm" color="primary" />
      <Box sx={{ flex: '1 1 16rem', minWidth: 0 }}>
        <Stack spacing={0.25}>
          <StrongValue>{name}</StrongValue>
          {originStep && <Caption>{originStep}</Caption>}
          <Caption>{dates}</Caption>
          {note && <Caption noWrap>{note}</Caption>}
          {refusalReason && (
            <Caption noWrap sx={{ color: `${statusColor}.main` }}>
              {refusalReason}
            </Caption>
          )}
        </Stack>
      </Box>
      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexShrink: 0, ml: 'auto' }}>
        <EnumChip set={EDocumentStatus} value={documentRequired.status} size="small" />
        {showResendIdentityVerification && (
          <ResendIdentityVerificationButton
            creditApplicationId={creditApplicationId}
            financingProgramId={financingProgramId}
            applicantType={documentRequired.applicantType}
            setError={setError}
          />
        )}
        {showUpload && (
          <UploadAreaButton
            color="primary"
            loading={isUploading}
            accept={Constants.SupportedDocumentTypes}
            onChange={onFilesPicked}
            size="small"
          >
            {t('viewCreditApplication.uploadDocument')}
          </UploadAreaButton>
        )}
      </Stack>
    </Stack>
  )
}

export default DocumentRow
